// Pure, dependency-free page-geometry constants + calc (RECUR-27304). Split
// out of exportToPdf.ts so consumers that only need the page-height math —
// e.g. the always-mounted usePageBoundaries overlay hook — don't
// transitively pull in the exporter's html2canvas/image-service import
// graph. exportToPdf.ts re-exports getDefaultPageHeightPx so its existing
// importers keep working; this file is a pure move, no behavior change.

// ── Why two page-aspect constants, not one ─────────────────────────────────
// PDF_PAGE_ASPECT (below) and SERVER_CONTENT_ASPECT (further down) both
// answer "how tall is one page", so they can look like an accidental
// duplicate — they aren't. Each is the SSOT for a DIFFERENT renderer:
// PDF_PAGE_ASPECT is exact A3 point-math for the RASTER (html2canvas slice)
// export path; SERVER_CONTENT_ASPECT is an empirically-measured aspect for
// the SERVER (headless-Chromium `@page`) path, calibrated against the real
// dev render Lambda. Chromium's scale-to-fit fills an A3 sheet differently
// than a raster slice, so the same page yields two different usable-height
// numbers ON PURPOSE — collapsing them into one constant would mispaginate
// whichever pipeline lost its own number. This file remains the single
// source for BOTH: exportToPdf.ts re-exports getDefaultPageHeightPx from
// here rather than defining its own.

// A3 portrait — wider than A4 so the 1229px editor content isn't crushed.
export const PAGE_WIDTH_PT = 842;
export const MARGIN_PT = 24;
export const PDF_CONTENT_WIDTH_PT = PAGE_WIDTH_PT - MARGIN_PT * 2;
// 1143pt = A3 height (1191pt) minus 2 × 24pt margin.
export const PDF_PAGE_ASPECT = 1143 / PDF_CONTENT_WIDTH_PT;

/**
 * The default (non-content-sized) page height in CSS px for a measured
 * content width, at the PDF export's A3 aspect ratio. The exporter's own
 * SSOT for "how tall is one page" — reused (not re-derived) by anything
 * that needs to reason about page boundaries.
 */
export function getDefaultPageHeightPx(contentWidthPx: number): number {
    return contentWidthPx * PDF_PAGE_ASPECT;
}

// ── Server export geometry (RECUR-27304 real-export calibration) ───────────
// TWO different page heights exist because TWO different exporters produce
// the PDF, with TWO different margins:
//   • RASTER (getDefaultPageHeightPx above) — html2canvas snapshot sliced by
//     `planPages`, margin = MARGIN_PT (24pt). This is the catch-fallback path
//     only (exportToPdfServer.ts's own comment: "the live path is the server
//     renderer") — untouched by this fix, still the raster SSOT.
//   • SERVER (below) — exportToPdfServer.ts's headless-Chromium Lambda,
//     native paged-media fragmentation against `@page { size: A3 portrait;
//     margin: 0.6in }` (exportToPdfServer.ts:346). This IS the page the
//     production "Export .pdf" button always renders (both One Pager and
//     Reports).
//
// RECUR-27304 history: this was originally derived the same way as the
// RASTER formula above — a width-scaled aspect ratio. A later pass
// mis-diagnosed a real export's overflow as proof the Lambda renders at a
// FIXED scale independent of content width, and replaced this with a fixed
// A3-mm/margin-derived constant — that "fix" was itself wrong: it made the
// deterministic export insert extra breaks and produce blank pages. A real
// dev render through the ACTUAL Lambda (RECUR-27304, pixel-ruler
// measurement) settled it: at content width 1229px, one A3 page holds
// ~1785px of that content. Chromium DOES scale the content to fit the page
// width — the same assumption the raster path already makes — so the usable
// height in CONTENT px scales WITH the content width; it is not a fixed
// physical value. Blank/overflow pages appear only when a page's content
// exceeds that measured capacity, so the capacity is expressed here as an
// empirically-measured aspect ratio, not an A3-mm/margin derivation.
export const SERVER_CONTENT_ASPECT = 1.4524; // = 1785px measured page capacity ÷ 1229px content width (real dev render, RECUR-27304).

// A small conservative UNDERSHOOT applied on top of the measured aspect
// ratio above. Once exportToPdfServer.ts INJECTS a forced break at every
// model page-start, the only dangerous direction is a model "page" that's
// actually TALLER than Chromium's real usable capacity at that width — that
// triggers an UNPLANNED extra break inside it. 0.98 is itself calibrated
// from the same real export referenced above (gives ~1749px at the One
// Pager's 1229px content width, safely under the ~1785px measured capacity)
// — it may still need re-validation against a wider export corpus (A-20/
// A-24) if pages keep overflowing, or if it turns out to waste too much
// trailing whitespace. Shared by the overlay (draws the seam) and the export
// (injects at the seam) via the SAME getServerPageHeightPx, so they can
// never disagree.
export const SERVER_PAGE_SAFETY_MARGIN = 0.98;

/**
 * The SERVER export's usable page height in CSS px for a measured content
 * width — the `pageBreakModel` fit-loop's page-height basis, undershot by
 * `SERVER_PAGE_SAFETY_MARGIN`. Distinct from `getDefaultPageHeightPx`
 * (raster); see the comment above for why.
 *
 * RECUR-27304: WIDTH-SCALED, calibrated from a real dev render — NOT a fixed
 * `@page`-box physical value (that hypothesis was tried and disproven; see
 * the history comment above). Chromium fits the editor's content to the
 * page width before paginating, so the usable height in content-px grows
 * with the content width, exactly like the raster path's own
 * `PDF_PAGE_ASPECT` already does.
 */
export function getServerPageHeightPx(contentWidthPx: number): number {
    return contentWidthPx * SERVER_CONTENT_ASPECT * SERVER_PAGE_SAFETY_MARGIN;
}

// ── PNO page-header geometry (RECUR-27604) ──────────────────────────────────
// A top-left letterhead logo repeated as PAGE CHROME on every page of the
// SERVER export. Values are CONTENT px / in (in-flow 1in = 96 content px); the
// physical size on the A3 sheet is smaller by Chromium's fit-to-width scale
// (~0.61x at the One Pager's 1229px content width), exactly like every other
// dimension in this document.
//
// Variant C (2026-08-14, wide letterhead + raised top): a FIXED-SIZE band box
// (width-led image inside it) replaces the earlier height-led sizing — any
// logo aspect renders aspect-true inside PNO_LOGO_WIDTH_IN x
// PNO_LOGO_MAX_HEIGHT_IN, anchored left (object-position: left center in
// exportToPdfServer.ts's `.pdf-pno-logo img` rule), so a wide letterhead
// lockup fills the width and a tall/square mark caps at the box height
// instead of both racing off two different unbounded dimensions.
export const PNO_LOGO_WIDTH_IN = 1.1; // band box width
export const PNO_LOGO_MAX_HEIGHT_IN = 0.5; // band box height (48 content px)
export const PNO_LOGO_GAP_BELOW_IN = 0.1875; // 18 content px, unchanged by variant C
/**
 * Per-page vertical space the header band consumes, in CSS content px —
 * reserved IDENTICALLY by the page-break model and the editor overlay (both
 * via `reservedPageTopPx`) so editor seams keep matching PDF breaks.
 * MEASURED, not assumed (RECUR-27604 spike, Chrome 151, A3 @ 0.6in, 1229px
 * pinned root): the wrapper table itself costs 0 px/page and the band costs
 * exactly its own CSS box height — bare 1722/1794/1794 → 1680/1752/1752 at
 * the original 0.25in-tall box. That spike fact is unchanged by variant C —
 * only the box's OWN height grew (0.25in -> 0.5in) — so the reservation
 * below is RE-DERIVED from the same box-height + gap arithmetic, not
 * re-measured.
 */
export const PNO_HEADER_BAND_PX = (PNO_LOGO_MAX_HEIGHT_IN + PNO_LOGO_GAP_BELOW_IN) * 96; // 66

// ── Logo-branch page-top-margin reclaim (RECUR-27604 mismatch fix) ─────────
// `exportToPdfServer.ts`'s `buildSelfContainedHtml` raises the exported
// page's TOP `@page` margin from `SERVER_PAGE_MARGIN_IN` (0.6in) to
// `SERVER_LOGO_PAGE_TOP_MARGIN_IN` (0.35in) ONLY when a logo is present — its
// own `pageMargin` assignment: `pnoLogoDataUrl ? '0.35in 0.6in 0.6in' :
// '0.6in'` (left/right/bottom stay 0.6in). But `SERVER_CONTENT_ASPECT` /
// `getServerPageHeightPx` above is calibrated against a real render at the
// UNIFORM 0.6in margin (see that section's own comment), so a logo-branch
// page physically holds `SERVER_PAGE_MARGIN_IN - SERVER_LOGO_PAGE_TOP_MARGIN_IN`
// = 0.25in MORE content than the model ever claims, whenever the band is
// reserved (`reservedPageTopPx > 0`). Undercounting real capacity isn't
// "safe slack" here: a single atomic unit (e.g. one tall table row) taller
// than the model's undercounted capacity but SHORTER than the true physical
// capacity trips the fit-loop's EXTEND path (`pageBreakModel.ts`) instead of
// packing onto a page of its own — extending a model "page" past what
// Chromium's native pagination can actually fit on one physical sheet, which
// then fragments it into an extra, unplanned page (RECUR-27604 — editor
// planned 3 pages, exported PDF rendered 4 for this exact reason).
// `getLogoPageTopReclaimPx` below is the fix: add the reclaimed height back
// into the model's capacity whenever the band is reserved, so the model's
// page height matches what the exported page physically holds.
//
// Named SERVER_* here — NOT a reuse of `PAGE_WIDTH_PT`/`MARGIN_PT` above,
// which are the RASTER path's own 24pt-margin constants (see this file's "Why
// two page-aspect constants" note) — this reclaim exists ONLY for the SERVER
// (`@page`) export path. If exportToPdfServer.ts's `pageMargin` literal
// (`'0.35in 0.6in 0.6in'` / `'0.6in'`) ever changes, these constants MUST
// change with it, or this reclaim silently drifts from what the exporter
// actually emits — cross-reference that assignment whenever either is
// touched.
export const SERVER_PAGE_MARGIN_IN = 0.6; // exportToPdfServer.ts's uniform (no-logo) @page margin, every side.
export const SERVER_LOGO_PAGE_TOP_MARGIN_IN = 0.35; // exportToPdfServer.ts's logo-branch @page TOP margin only.
// A3 portrait width (841.92pt — the same physical sheet PDF_PAGE_ASPECT's own
// comment cites) minus the SAME 0.6in side margins on BOTH sides, in points
// (0.6in x 72pt/in x 2 sides = 86.4pt): 841.92 - 86.4 = 755.52pt of physical
// content width. This is the SERVER path's own content-width-in-points — the
// RASTER path's PDF_CONTENT_WIDTH_PT (842 - 24*2 = 794pt) is a different
// number for a different margin, never reused here.
export const SERVER_PAGE_CONTENT_WIDTH_PT = 841.92 - SERVER_PAGE_MARGIN_IN * 72 * 2; // 755.52
// The extra top margin the logo branch's page physically gains back, in
// points: (0.6 - 0.35)in x 72pt/in = 18pt.
const SERVER_LOGO_PAGE_TOP_RECLAIM_PT = (SERVER_PAGE_MARGIN_IN - SERVER_LOGO_PAGE_TOP_MARGIN_IN) * 72; // 18

/**
 * The logo-branch page-top-margin reclaim (RECUR-27604), in CONTENT px, for a
 * measured content width — the height `getServerPageHeightPx` undercounts
 * whenever `exportToPdfServer.ts` reserves the header band
 * (`reservedPageTopPx > 0`; see this section's own module comment for why).
 * WIDTH-SCALED like every other server-path dimension: the fixed 18pt
 * physical reclaim converts to content px via the SAME fit-to-width scale
 * (`SERVER_PAGE_CONTENT_WIDTH_PT` physical pt ÷ `contentWidthPx` content px)
 * `getServerPageHeightPx` itself is built on, so
 * `getLogoPageTopReclaimPx(1229) ≈ 29.3` — cross-checked against a real dev
 * render (RECUR-27604).
 */
export function getLogoPageTopReclaimPx(contentWidthPx: number): number {
    if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) return 0;
    return (SERVER_LOGO_PAGE_TOP_RECLAIM_PT * contentWidthPx) / SERVER_PAGE_CONTENT_WIDTH_PT;
}
